import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import apiUrls from "../../../api";
import { getConnectedDevice, getInstallJob } from "../state/deviceInstallerSlice";
import { detectDeviceFamily } from "../utils/platformCompat";
import { getPackageVersionName, compareVersions } from "../utils/versionName";
import {
  SPATIAL_STUDIO_PACKAGE_NAME,
  SPATIAL_STUDIO_PLATFORM_IDS,
  POLL_INTERVAL_MS,
} from "../constants";
import { IEditorVrBuild } from "../../../model/model";

export type SpatialStudioStatus =
  | "loading"
  | "not_available"
  | "not_installed"
  | "up_to_date"
  | "update_available";

export interface SpatialStudioState {
  status: SpatialStudioStatus;
  installedVersion: string | null;
  latestVersion: string | null;
  latestBuild: IEditorVrBuild | null;
  s3Url: string | null;
  refresh: () => void;
}

export function useSpatialStudioStatus(): SpatialStudioState {
  const device = useSelector(getConnectedDevice);
  const installJob = useSelector(getInstallJob);
  const isReady = device?.status === "ready";
  const deviceFamily = isReady ? detectDeviceFamily(device.productName) : null;

  const [status, setStatus] = useState<SpatialStudioStatus>("loading");
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [latestBuild, setLatestBuild] = useState<IEditorVrBuild | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [s3Url, setS3Url] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [buildsLoaded, setBuildsLoaded] = useState(false);

  const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);

  // Fetch latest builds from API (once)
  const buildsRef = useRef<IEditorVrBuild[]>([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(apiUrls.getMostRecentEditorVrBuildPerPlatform)
      .then((res) => {
        if (!cancelled) {
          buildsRef.current = res.data || [];
          setBuildsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setBuildsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Determine status when device is ready
  useEffect(() => {
    if (!isReady || !deviceFamily || !buildsLoaded) {
      setStatus("loading");
      setInstalledVersion(null);
      return undefined;
    }

    const platformId = SPATIAL_STUDIO_PLATFORM_IDS[deviceFamily];
    if (platformId === undefined) {
      setStatus("not_available");
      return undefined;
    }

    const build = buildsRef.current.find((b) => b.platform_id?.id === platformId);

    if (!build) {
      setStatus("not_available");
      setLatestBuild(null);
      setLatestVersion(null);
      setS3Url(null);
      return undefined;
    }

    setLatestBuild(build);
    setLatestVersion(build.version ?? null);
    setS3Url(build.source_zip?.s3_url ?? null);

    let cancelled = false;
    getPackageVersionName(SPATIAL_STUDIO_PACKAGE_NAME)
      .then((version) => {
        if (cancelled) return;
        setInstalledVersion(version);
        if (!version) {
          setStatus("not_installed");
        } else if (compareVersions(version, build.version) < 0) {
          setStatus("update_available");
        } else {
          setStatus("up_to_date");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("not_installed");
      });

    return () => {
      cancelled = true;
    };
  }, [isReady, deviceFamily, refreshCounter, buildsLoaded]);

  // Poll installed version periodically (like package scanner)
  const isInstalling =
    installJob != null && installJob.status !== "success" && installJob.status !== "error";

  useEffect(() => {
    if (!isReady || isInstalling) return undefined;

    const id = setInterval(() => {
      refresh();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isReady, isInstalling, refresh]);

  return { status, installedVersion, latestVersion, latestBuild, s3Url, refresh };
}
